import { CornerDownRight } from 'lucide-react';
import Button from '@/components/Button';
import { LinkifiedText } from '@/components/LinkifiedText';
import type { GoalTaskBlock } from '@/entities/chat-message';
import { useGoalsByIds } from '@/queries/goal';

interface TaskCardProps {
  task: GoalTaskBlock;
  messageId: string;
  blockIndex: number;
  onAddToPlan?: (messageId: string, blockIndex: number) => void;
  onSkip?: (messageId: string, blockIndex: number) => void;
  disabled?: boolean;
}

export function TaskCard({
  task,
  messageId,
  blockIndex,
  onAddToPlan,
  onSkip,
  disabled,
}: TaskCardProps) {
  const { goals } = useGoalsByIds([task.data.parentGoalId], {
    source: 'TaskCard.parentGoal',
  });
  const parentGoal = goals?.[0];
  const isLoading = goals === undefined;

  return (
    <div className="min-w-0 rounded-lg border border-gray-300 bg-white p-3">
      <span className="text-[11px] font-medium tracking-wide text-blue-500 uppercase">
        Suggested task
      </span>
      {isLoading ? (
        <div className="mt-1 h-4 w-32 animate-pulse rounded bg-gray-100" />
      ) : (
        <>
          <p className="mt-1 text-[14px] font-medium text-gray-900">
            <LinkifiedText text={task.data.title} />
          </p>
          {parentGoal && (
            <div className="mt-1 flex min-w-0 items-baseline gap-1">
              <CornerDownRight className="mt-[0.15em] h-3.5 w-3.5 shrink-0 text-gray-500" />
              <span className="truncate text-[13px] text-gray-600">
                {parentGoal.title}
              </span>
            </div>
          )}
        </>
      )}
      {onAddToPlan && (
        <div className="mt-3 flex justify-end gap-2">
          {onSkip && (
            <Button
              variant="ghost"
              onClick={() => {
                onSkip(messageId, blockIndex);
              }}
              disabled={disabled}
              size="sm"
              className="border-slate-300 text-xs text-slate-600 hover:text-slate-950"
            >
              Skip
            </Button>
          )}
          <Button
            onClick={() => {
              onAddToPlan(messageId, blockIndex);
            }}
            disabled={disabled}
            size="sm"
            className="bg-blue-600 text-xs text-white hover:bg-blue-700"
          >
            Add to plan
          </Button>
        </div>
      )}
    </div>
  );
}
